import { api } from './api.ts';

/** Projects and coding agents on the daemon (design-docs/0030). Types mirror packages/core/src/agents.ts. */
export interface ProjectInfo {
  slug: string;
  name: string;
  description: string;
  repository: string | null;
  baseBranch: string;
  linearOrg: string | null;
  previewCommand: string | null;
  createdAt: number;
}

export type AgentStatus = 'starting' | 'running' | 'asking' | 'idle' | 'stopped' | 'failed';

export interface AgentInfo {
  id: string;
  project: string;
  task: string;
  title: string;
  status: AgentStatus;
  branch: string;
  sessionId: string | null;
  preview: { state: 'none' | 'starting' | 'ready' | 'failed'; url: string | null; error?: string };
  question: { id: string; text: string } | null;
  activity: Array<{ at: number; text: string }>;
  talkSessionId: string | null;
  summary: string | null;
  /** screenshots it showed the owner (design-docs/0031) */
  shots?: Array<{ name: string; caption: string; at: number }>;
  /** its browser can be watched live (design-docs/0031) */
  browser?: { live: boolean };
  createdAt: number;
  updatedAt: number;
}

export const agentsApi = {
  projects: () => api.get<ProjectInfo[]>('/projects'),
  project: (slug: string) => api.get<ProjectInfo & { agents: AgentInfo[] }>(`/projects/${encodeURIComponent(slug)}`),
  createProject: (input: { name: string; description?: string; repository?: string }) =>
    api.post<ProjectInfo>('/projects', input),
  list: () => api.get<AgentInfo[]>('/agents'),
  get: (id: string) => api.get<AgentInfo>(`/agents/${encodeURIComponent(id)}`),
  start: (project: string, task: string, title?: string) =>
    api.post<AgentInfo>(`/projects/${encodeURIComponent(project)}/agents`, title ? { task, title } : { task }),
  message: (id: string, text: string) => api.post<AgentInfo>(`/agents/${encodeURIComponent(id)}/message`, { text }),
  answer: (id: string, text: string) => api.post<AgentInfo>(`/agents/${encodeURIComponent(id)}/answer`, { text }),
  stop: (id: string) => api.post<AgentInfo>(`/agents/${encodeURIComponent(id)}/stop`),
  preview: (id: string) => api.post<AgentInfo>(`/agents/${encodeURIComponent(id)}/preview`),
};

/** Lists put what needs the owner first, then what is working, then the rest; newest first within each. */
const RANK: Record<AgentStatus, number> = { asking: 0, running: 1, starting: 1, idle: 2, failed: 3, stopped: 4 };
export const byUrgency = (a: AgentInfo, b: AgentInfo) => RANK[a.status] - RANK[b.status] || b.updatedAt - a.updatedAt;
export const isActive = (a: AgentInfo) => a.status === 'running' || a.status === 'starting';

export const agentCounts = (agents: AgentInfo[]) => ({
  running: agents.filter(isActive).length,
  asking: agents.filter((a) => a.status === 'asking').length,
  previews: agents.filter((a) => a.preview.state === 'ready').length,
});

/** The project a talk is about, remembered on this device (Home's switcher, a project page's talk button). */
export function talkProject(slug?: string | null): string | null {
  try {
    if (slug === null) localStorage.removeItem('talkProject');
    else if (slug !== undefined) localStorage.setItem('talkProject', slug);
    return localStorage.getItem('talkProject');
  } catch {
    return slug ?? null;
  }
}

/** Calls `fn` every `ms` while `el` is in the page (there is no agent event yet); the caller renders the first state. */
export function pollWhile(el: HTMLElement, ms: number, fn: () => Promise<void>) {
  let busy = false;
  const tick = async () => {
    if (busy) return;
    busy = true;
    await fn().catch(() => {});
    busy = false;
  };
  let seen = false; // the view may be built before it is mounted
  const t = setInterval(() => {
    if (el.isConnected) {
      seen = true;
      void tick();
    } else if (seen) clearInterval(t);
  }, ms);
}
